import React, { useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { MessageSquare, X } from "lucide-react";
import AgentChat from "./AgentChat";
import { useRBAC } from "../hooks/useRBAC";

// The AI Intelligence "Ask" drawer: the same AI agent as /agent, composed in compact layout and
// scoped to AI Intelligence (`context.scope = "ai-intelligence"`). The backend narrows the tool
// set, prompt and SQL whitelist to the module for that scope and injects `context.assetId` into
// the asset-detail report, so "this asset" in a question means the asset the user has expanded.
// Text only; no conversation persistence — the chat unmounts (and resets) when the drawer closes.

export const SUGGESTIONS = [
  "What needs attention today?",
  "How is our AI posture trending?",
  "Which assets are untiered?",
  "What did we spend on AI in the last 30 days?",
];

export const ASSET_SUGGESTIONS = [
  "Why is this asset flagged?",
  "What does this asset's playbook say?",
];

export const READ_ONLY_NOTE =
  "Read-only. The agent can look up your AI Intelligence data; it cannot change tiers, comments or containment.";

// The header button that opens the drawer. Owns the RBAC gate so it is testable in isolation:
// renders nothing unless the user may run the agent (frontend `canRunAgent` = ORG VIEWER+; the
// backend `CanRunAgent` check on POST /Agent/query is the enforcement boundary).
export function AskButton({ orgId, onClick, active = false }) {
  const { canRunAgent } = useRBAC();
  if (!canRunAgent(orgId)) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-2 rounded-md border border-border hover:bg-theme-hover ${active ? "bg-theme-hover" : ""}`}
      title="Ask about AI Intelligence"
      aria-label="Ask about AI Intelligence"
    >
      <MessageSquare className="w-5 h-5 text-content-secondary" />
    </button>
  );
}

export default function AiAskDrawer({ open, onClose, orgId, assetId, tab }) {
  const panelRef = useRef(null);

  // Hooks first — the early return below must come after every hook (rules-of-hooks lint is off
  // in this repo; a hook below a return ships as React #310).
  const context = useMemo(
    () => ({ scope: "ai-intelligence", assetId: assetId ?? undefined, tab }),
    [assetId, tab],
  );

  const suggestions = useMemo(
    () => (assetId != null ? [...SUGGESTIONS, ...ASSET_SUGGESTIONS] : SUGGESTIONS),
    [assetId],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Focus the chat input on open. AgentChat owns the textarea, so reach it through the panel
  // rather than adding a ref prop to the shared component.
  useEffect(() => {
    if (open) panelRef.current?.querySelector("textarea")?.focus();
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-overlay backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
        data-testid="ai-ask-drawer-backdrop"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-ask-drawer-title"
        className="absolute inset-y-0 right-0 w-full sm:w-[420px] bg-theme-secondary border-l border-border shadow-theme-xl flex flex-col h-full"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 id="ai-ask-drawer-title" className="text-base font-semibold text-content-primary flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-content-secondary" />
            Ask about AI Intelligence
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-content-muted hover:text-content-primary hover:bg-theme-hover transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 flex flex-col">
          <AgentChat
            compact
            context={context}
            suggestions={suggestions}
            placeholder="Ask about your AI estate…"
          />
        </div>
        <div className="px-4 py-2 border-t border-border text-xs text-content-muted">
          {READ_ONLY_NOTE}
        </div>
      </div>
    </div>,
    document.body,
  );
}
